// AppID 配置管理页面 — 添加/修改/验证微信公众号/小程序 AppID
import { useState } from 'react'
import { Card, Table, Button, Modal, Form, Input, Switch, Space, Tag, App } from 'antd'
import { PlusOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post, api } from '@/lib/api'
import PageHeader from '@/components/PageHeader'

interface WechatConfig {
  id: number
  appId: string
  appSecret: string
  name: string
  description: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export default function WechatConfigPage() {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [verifyResult, setVerifyResult] = useState<{ valid: boolean; message: string } | null>(null)
  const [form] = Form.useForm()

  const { data, isLoading } = useQuery({
    queryKey: ['wechat-configs'],
    queryFn: () => get<{ items: WechatConfig[] }>('/wechat-config/'),
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      if (editingId) {
        return api.put(`/wechat-config/${editingId}`, values).then(r => r.data)
      }
      return post('/wechat-config/', values)
    },
    onSuccess: () => {
      message.success(editingId ? '配置已更新' : '配置已添加')
      queryClient.invalidateQueries({ queryKey: ['wechat-configs'] })
      setModalOpen(false)
      setEditingId(null)
      setVerifyResult(null)
      form.resetFields()
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      message.error(detail || '保存失败')
    },
  })

  async function handleVerify() {
    try {
      const values = await form.validateFields(['appId', 'appSecret'])
      setVerifying(true)
      setVerifyResult(null)
      const result = await post<{ valid: boolean; message: string }>('/wechat-config/verify', {
        appId: values.appId,
        appSecret: values.appSecret,
        name: values.name || 'test',
      })
      setVerifyResult(result)
    } catch {
      message.warning('请先填写 AppID 和 AppSecret')
    } finally {
      setVerifying(false)
    }
  }

  function openAdd() {
    setEditingId(null)
    setVerifyResult(null)
    form.resetFields()
    form.setFieldsValue({ isActive: false })
    setModalOpen(true)
  }

  function openEdit(record: WechatConfig) {
    setEditingId(record.id)
    setVerifyResult(null)
    form.setFieldsValue({
      appId: record.appId,
      appSecret: record.appSecret,
      name: record.name,
      description: record.description,
      isActive: record.isActive,
    })
    setModalOpen(true)
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', width: 180 },
    { title: 'AppID', dataIndex: 'appId', width: 220, ellipsis: true },
    { title: 'AppSecret', dataIndex: 'appSecret', width: 180, ellipsis: true,
      render: (v: string) => v?.slice(0, 8) + '****' },
    {
      title: '状态', dataIndex: 'isActive', width: 80,
      render: (v: boolean) => v
        ? <Tag color="green">启用</Tag>
        : <Tag color="default">停用</Tag>,
    },
    {
      title: '操作', key: 'action', width: 80,
      render: (_: unknown, record: WechatConfig) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="AppID 配置管理" subtitle="管理微信公众号/小程序 AppID 与 Secret" />

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            添加配置
          </Button>
        </div>

        <Table
          dataSource={data?.items || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal
        title={editingId ? '编辑配置' : '添加配置'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setVerifyResult(null); form.resetFields() }}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        width={520}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如: 宁波ifmall小程序" />
          </Form.Item>

          <Form.Item name="appId" label="AppID" rules={[{ required: true, message: '请输入 AppID' }]}>
            <Input placeholder="wx..." />
          </Form.Item>

          <Form.Item name="appSecret" label="AppSecret" rules={[{ required: true, message: '请输入 AppSecret' }]}>
            <Input.Password placeholder="AppSecret" />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="可选描述" />
          </Form.Item>

          <Form.Item name="isActive" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Space>
            <Button onClick={handleVerify} loading={verifying}>
              <CheckCircleOutlined /> 验证配置
            </Button>
          </Space>

          {verifyResult && (
            <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6,
              background: verifyResult.valid ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${verifyResult.valid ? '#b7eb8f' : '#ffccc7'}` }}>
              {verifyResult.valid
                ? <span style={{ color: '#52c41a' }}><CheckCircleOutlined /> {verifyResult.message}</span>
                : <span style={{ color: '#ff4d4f' }}><CloseCircleOutlined /> {verifyResult.message}</span>
              }
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}
